import axios from 'axios';
import {
  getUserTimezone,
  TimezoneProperty
} from '../modules/timezone';
import {
  prepareMeetingDataForStorage,
  prepareDataFromStorage
} from '../modules/timezone/migration';
import { standardizeTimeZoneProperty } from '../modules/timezone';
// Define API URL
const API_URL = `${process.env.REACT_APP_API_URL}/api`;

// Schedule-related interfaces (matching backend structure)
export interface WorkWeekDay {
  enabled: boolean;
  intervals: Array<{
    id: string;
    startTime: string; // Format: "HH:MM" 24h
    endTime: string; // Format: "HH:MM" 24h
  }>;
}

export interface DefaultWorkWeek {
  monday: WorkWeekDay;
  tuesday: WorkWeekDay;
  wednesday: WorkWeekDay;
  thursday: WorkWeekDay;
  friday: WorkWeekDay;
  saturday: WorkWeekDay;
  sunday: WorkWeekDay;
}

// Define Organization interface for color hierarchy support
export interface Organization {
  id: number;
  name: string;
  logoUrl?: string;
  brandColor?: string;
}

// Define user interface
export interface User {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  profilePicture?: string;
  profileImage?: string;
  jobTitle?: string;
  title?: string;
  department?: string;
  company?: string;
  organizationName?: string;
  organizationId?: number;
  coverPhoto?: string;
  coverPhotoMobile?: string;
  brandColor?: string;
  displayLanguage?: 'en' | 'ja';
  hideSailLabBranding?: boolean;
  // Organization association for color hierarchy: meeting > organization > user > system
  organization?: Organization;
}

// Host information interface
export interface HostInfo {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  jobTitle?: string;
  title?: string;
  department?: string;
  company?: string;
  organizationName?: string;
  // URL fields for booking link construction
  username?: string;
  customUrl?: string;
  // Role and requirement fields (from EventTypeHost)
  role?: 'primary' | 'fixed' | 'round_robin';
  requirement?: 'required' | 'optional';
  // Organization role (owner, admin, member, guest)
  organizationRole?: 'owner' | 'admin' | 'member' | 'guest';
}

// Define meeting type interface
/**
 * The kinds of meeting type the product supports.
 *
 * Declared once and imported everywhere. This union used to be written out by
 * hand in four separate files, which is how one of them ends up missing a kind
 * and the compiler starts rejecting valid code. Mirrors `MeetingTypeEnum` in
 * server/src/models/meetingType.ts — change both together.
 */
export type MeetingTypeKind =
  | 'individual'
  | 'collective'
  | 'pooled'
  | 'round_robin'
  | 'event'
  | 'service'
  | 'poll';

export interface MeetingType {
  id: number;
  userId: number;
  teamId?: number; // Team ID for team-based meeting types
  user?: User; // Add user property for populated data
  title: string;
  description: string;
  duration: number[]; // Array of available duration options in minutes
  defaultDuration?: number | null; // Pre-selected duration on first booking-page load (must be in `duration`)
  slug: string;
  color?: string;
  isActive: boolean;
  type?: MeetingTypeKind;
  /**
   * Poll rows only. A poll has no booking page — it is shared as
   * /poll/{pollPublicId} — so the card must build its link from this rather
   * than from {username}/{slug}.
   */
  pollPublicId?: string;
  location?: {
    type: 'online' | 'phone' | 'inperson';
    provider?: 'zoom' | 'meet' | 'teams';
    address?: string;
  };
  pricing: {
    type: 'free' | 'one-time' | 'recurring';
    amount?: number;
    currency?: string;
  };
  // Phase 2 — host payment collection
  paymentSettings?: import('../components/MeetingTypeForm/types').PaymentSettings | null;
  accessRequirement?: import('../components/MeetingTypeForm/types').AccessRequirement;
  pointCost?: number | null;
  // Derived on the list payload: true when payment is enabled AND the linked
  // Stripe account is connected + verified; false → show "Please reconnect"
  // pill; undefined when payment is disabled. (Read-only, server-computed.)
  paymentAccountConnected?: boolean;
  maxBookingsPerDay?: number;
  requiresApproval?: boolean;
  minimumBookingNotice?: number; // in hours
  maximumBookingWindow?: number; // in days
  bufferTimeBefore?: number; // in minutes
  bufferTimeAfter?: number; // in minutes
  slotIncrementMinutes?: number; // Time interval between generated slots (e.g., 15, 30, 60)
  availabilitySchedule: {
    days: number[];
    startTime: string;
    endTime: string;
  }[];
  timeZone: TimezoneProperty; // Standardized timezone property
  scheduleId?: number | null; // Reference to the assigned availability schedule (for preview mode)
  calendarConnection?: {
    provider: string;
    isConnected: boolean;
    email?: string;
  };
  isVirtual?: boolean;
  isInPerson?: boolean; // NEW: Flag for in-person meetings
  videoConferenceProvider?: 'google_meet' | 'zoom' | 'microsoft_teams';
  // Location fields for in-person and phone meetings
  locationDetails?: string; // Physical address for in-person meetings
  phoneNumber?: string; // Phone number for phone call meetings
  countryCode?: string; // Country code for phone calls (e.g., '+1', '+44')
  meetingNotes?: string; // Optional instructions for in-person or phone meetings
  showLocationAfterBooking?: boolean; // Privacy control: show location only after booking confirmed
  activeSchedule?: {
    id: number;
    name: string;
    isPrimary: boolean;
    timeZone: string;
    defaultWorkWeek?: DefaultWorkWeek;
  } | null;
  // Host information
  host?: HostInfo; // Primary host information (defaults to meeting creator)
  hosts?: HostInfo[]; // Array of all hosts
  createdBy?: HostInfo; // Meeting creator information
  hostDisplaySettings?: {
    showHostDetails: 'host' | 'company' | 'none';
    detailsToShow?: string[];
    displayPosition: 'above_title' | 'below_description' | 'end_of_details';
    multiHostAvatarMode?: 'single' | 'multiple'; // For multi-host meetings: show single primary or all hosts
    selectedDepartmentValue?: string | null; // For multi-host with 'multiple' mode: selected department to display
    socialMediaDisplayPosition?: 'above_avatar' | 'below_avatar' | 'below_description' | 'bottom'; // Where to display social media links
    // Per-meeting-type text overrides for host detail fields (never mutates org/user data)
    detailOverrides?: { name?: string; title?: string; department?: string; company?: string };
  };
  // Styling & Branding
  brandColor?: string;
  displayLanguage?: 'en' | 'ja';
  coverPhotoUrl?: string;
  coverPhotoUrlMobile?: string;
  twitterUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  lineUrl?: string;
  websiteUrl?: string;
  socialMediaOrder?: string[]; // Order of social media links display
  // Single-use link fields
  isOneTimeUse?: boolean;
  parentMeetingTypeId?: number;
  maxBookings?: number;
  bookingCount?: number;
  // Booking redirect
  bookingRedirectUrl?: string; // URL to redirect guests after booking completion
  // Form template for custom booking forms (UUID string)
  formTemplateId?: string | null;
  // Event booking capacity settings
  maxCapacity?: number; // Default capacity for event booking slots (min: 2, max: 9999)
  showSlotsLeftEnabled?: boolean; // Toggle to show "X slots left" indicator to guests
  showSlotsLeftThreshold?: number; // Show indicator only when slots left <= this value (default: 10)
  waitlistEnabled?: boolean; // Allow guests to join a waitlist when an event slot is full
  // Service booking settings
  serviceMode?: 'options' | 'duration'; // 'options' = service menu, 'duration' = simple duration selection
  // Access control flags (set by backend)
  isOwner?: boolean;
  isCoHost?: boolean;
  isShared?: boolean;
  sharePermission?: 'view' | 'edit' | 'manage' | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateMeetingTypeDto {
  title: string;
  description?: string;
  duration: number[]; // Array of available duration options in minutes
  defaultDuration?: number | null; // Pre-selected duration on first booking-page load (must be in `duration`)
  slug?: string;
  color?: string;
  type?: MeetingTypeKind;
  // Organization context - CRITICAL: Must be passed to ensure meeting type is created in correct org
  organizationId?: number;
  maxCapacity?: number; // Event booking capacity (default: 100, min: 2, max: 9999)
  // DEPRECATED: Legacy location object - use isVirtual, isInPerson, videoConferenceProvider, etc. instead
  // Kept for backward compatibility but no longer sent to backend
  location?: {
    type: 'online' | 'phone' | 'inperson';
    provider?: 'zoom' | 'meet' | 'teams';
    address?: string;
  };
  pricing?: {
    type: 'free' | 'one-time' | 'recurring';
    amount?: number;
    currency?: string;
  };
  requiresApproval?: boolean;
  minimumBookingNotice?: number; // in hours
  maximumBookingWindow?: number; // in days
  bufferTimeBefore?: number; // in minutes
  bufferTimeAfter?: number; // in minutes
  slotIncrementMinutes?: number; // Time interval between generated slots (e.g., 15, 30, 60)
  // Legacy availability schedule (deprecated - use scheduleId instead)
  availabilitySchedule?: {
    days: number[];
    startTime: string;
    endTime: string;
  }[];
  // New schedule system
  scheduleId?: number | string | null; // Reference to shared schedule, null for custom
  customScheduleData?: {
    timeZone: string;
    defaultWorkWeek: DefaultWorkWeek;
  }; // Custom schedule data when scheduleId is null
  isActive?: boolean;
  timeZone?: string; // Standardized to timeZone
  // Location fields - Use these instead of legacy location object
  isVirtual?: boolean;
  videoConferenceProvider?: 'google_meet' | 'zoom' | 'microsoft_teams';
  hostDisplaySettings?: {
    showHostDetails: 'host' | 'company' | 'none';
    detailsToShow?: string[];
    displayPosition: 'above_title' | 'below_description' | 'end_of_details';
    multiHostAvatarMode?: 'single' | 'multiple'; // For multi-host meetings: show single primary or all hosts
    selectedDepartmentValue?: string | null; // For multi-host with 'multiple' mode: selected department to display
    socialMediaDisplayPosition?: 'above_avatar' | 'below_avatar' | 'below_description' | 'bottom'; // Where to display social media links
    // Per-meeting-type text overrides for host detail fields (never mutates org/user data)
    detailOverrides?: { name?: string; title?: string; department?: string; company?: string };
  };
  // Styling & Branding fields - CRITICAL: Must match MeetingType interface
  brandColor?: string;
  displayLanguage?: 'en' | 'ja';
  coverPhotoUrl?: string;
  coverPhotoUrlMobile?: string;
  twitterUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  lineUrl?: string;
  websiteUrl?: string;
  socialMediaOrder?: string[]; // Order of social media links display
  // Location fields for in-person and phone meetings - CRITICAL: Must match MeetingType interface
  isInPerson?: boolean;
  locationDetails?: string;
  phoneNumber?: string;
  countryCode?: string;
  meetingNotes?: string;
  showLocationAfterBooking?: boolean;
  bookingRedirectUrl?: string;
  formTemplateId?: string | null;
  // Event booking capacity settings
  showSlotsLeftEnabled?: boolean; // Toggle to show "X slots left" indicator to guests
  showSlotsLeftThreshold?: number; // Show indicator only when slots left <= this value (default: 10)
  waitlistEnabled?: boolean; // Allow guests to join a waitlist when an event slot is full
  // Service booking settings
  serviceMode?: 'options' | 'duration'; // 'options' = service menu, 'duration' = simple duration selection
  // Communication template group (which email templates to use for this meeting type)
  defaultCommunicationTemplateGroup?: string | null;
  // Host payment collection (Phase 2) — null/undefined means disabled
  paymentSettings?: import('../components/MeetingTypeForm/types').PaymentSettings | null;
  accessRequirement?: import('../components/MeetingTypeForm/types').AccessRequirement;
  pointCost?: number | null;
}

class MeetingTypeService {
  private getAuthHeader(token: string) {
    if (!token) {
      return {
        headers: {}
      };
    }

    // Ensure token doesn't have any extra spaces or line breaks
    const cleanToken = token.trim();

    return {
      headers: {
        Authorization: `Bearer ${cleanToken}`
      }
    };
  }

  // NORTH STAR: All caching now handled by backend
  // Client-side slot caching removed in favor of backend caching

  /**
   * Get all meeting types for the current user (LIST VIEW)
   * API: GET /api/meeting-types
   * Purpose: Fetches basic meeting type data for displaying in cards/list
   * Used in: MeetingTypes page list view
   * Note: Returns minimal data, does NOT include full details like description
   */
  async getMeetingTypes(token: string): Promise<MeetingType[]> {
    try {
      const response = await axios.get(
        `${API_URL}/meeting-types`,
        this.getAuthHeader(token)
      );

      return response.data.meetingTypes;
    } catch (error) {
      throw new Error('Failed to fetch meeting types');
    }
  }

  /**
   * Get meeting type limits and feature access for the current user
   * API: GET /api/meeting-types/limits
   * Purpose: Check if user can create more meeting types and which types are available
   * Used in: Before opening Create Meeting Wizard
   */
  async getMeetingTypeLimits(token: string): Promise<{
    limit: number | null;
    currentUsage: number;
    canCreate: boolean;
    features: {
      oneOnOne: boolean;
      roundRobin: boolean;
      event: boolean;
      service: boolean;
      multiHost: boolean;
    };
  }> {
    try {
      const response = await axios.get(
        `${API_URL}/meeting-types/limits`,
        this.getAuthHeader(token)
      );
      // Ensure service and multiHost properties exist (for backwards compatibility)
      return {
        ...response.data,
        features: {
          ...response.data.features,
          service: response.data.features.service ?? false,
          multiHost: response.data.features.multiHost ?? false,
        },
      };
    } catch (error) {
      // Default to allowing creation if we can't fetch limits
      return {
        limit: null,
        currentUsage: 0,
        canCreate: true,
        features: {
          oneOnOne: true,
          roundRobin: false,
          event: false,
          service: false,
          multiHost: false,
        },
      };
    }
  }

  /**
   * Get a specific meeting type by ID (EDIT/DETAIL VIEW)
   * API: GET /api/meeting-types/:id
   * Purpose: Fetches COMPLETE meeting type data including all fields
   * Used in: Edit panel when user clicks "Edit" on a meeting type
   * Note: Returns full details including description, schedules, etc.
   */
  async getMeetingType(token: string, id: string | number): Promise<MeetingType> {
    if (!token || !id) {
      throw new Error('Missing required parameters: token and id');
    }

    try {
      const response = await axios.get(
        `${API_URL}/meeting-types/${id}`,
        this.getAuthHeader(token)
      );

      if (!response.data || !response.data.meetingType) {
        throw new Error('Meeting type not found');
      }

      // Get the user's timezone
      const userTimezone = getUserTimezone();

      // Standardize the timezone property
      const standardizedData = standardizeTimeZoneProperty(response.data.meetingType);

      // Preserve activeSchedule and other important fields
      const meetingTypeData = prepareDataFromStorage(standardizedData, userTimezone);

      // Ensure activeSchedule is preserved
      if (response.data.meetingType.activeSchedule) {
        meetingTypeData.activeSchedule = response.data.meetingType.activeSchedule;
      }

      return meetingTypeData;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Meeting type not found');
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch meeting type');
    }
  }

  /**
   * Get a meeting type by username and slug (public endpoint)
   * Uses /api/booking path to avoid conflicts with authenticated admin routes.
   * Returns meeting type data only. Availability is fetched separately
   * via batch-availability (with persistent DB cache for speed).
   */
  async getMeetingTypeBySlug(username: string, slug: string): Promise<MeetingType> {
    try {
      // Get the user's timezone
      const userTimezone = getUserTimezone();

      const response = await axios.get(`${API_URL}/booking/${username}/${slug}`, {
        params: { timezone: userTimezone },
      });

      // Standardize the timezone property
      const standardizedData = standardizeTimeZoneProperty(response.data);

      // Prepare the response data for use in the UI
      const finalData = prepareDataFromStorage(standardizedData, userTimezone);
      return finalData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new meeting type
   */
  async createMeetingType(token: string, meetingType: CreateMeetingTypeDto): Promise<MeetingType> {
    try {
      // Get the creator's timezone
      const creatorTimezone = getUserTimezone();

      // Prepare the meeting data with proper timezone handling
      const preparedData = prepareMeetingDataForStorage(meetingType, creatorTimezone);

      const response = await axios.post(
        `${API_URL}/meeting-types`,
        preparedData,
        this.getAuthHeader(token)
      );

      // Prepare the response data for use in the UI
      return prepareDataFromStorage(response.data.meetingType, creatorTimezone);
    } catch (error: any) {
      // Pass through the backend's actual error message for better debugging
      const backendMessage = error.response?.data?.message || error.response?.data?.error;
      throw new Error(backendMessage || 'Failed to create meeting type');
    }
  }

  /**
   * Update a meeting type
   */
  async updateMeetingType(token: string, id: string | number, meetingType: Partial<CreateMeetingTypeDto>): Promise<MeetingType> {
    try {
      // Get the creator's timezone
      const creatorTimezone = getUserTimezone();

      // Create a new object that includes both timezone property formats
      const requestData = {
        ...meetingType,
        // Force both formats to be included with the same value
        timeZone: meetingType.timeZone || creatorTimezone,
      };

      // Prepare the meeting data with proper timezone handling
      const preparedData = prepareMeetingDataForStorage(requestData, creatorTimezone);

      // Add both timezone properties again to be absolutely sure they're included
      preparedData.timeZone = requestData.timeZone;

      const response = await axios.put(
        `${API_URL}/meeting-types/${id}`,
        preparedData,
        this.getAuthHeader(token)
      );

      // Make a copy of the response data we can safely modify
      const responseData = {
        ...response.data.meetingType,
        // Ensure both timezone properties exist with the correct value
        timeZone: response.data.meetingType.timeZone || requestData.timeZone,
      };

      // Prepare the response data for use in the UI
      return prepareDataFromStorage(responseData, creatorTimezone);
    } catch (error: any) {
      // Pass through the backend's actual error message for better debugging
      const backendMessage = error.response?.data?.message || error.response?.data?.error;
      throw new Error(backendMessage || 'Failed to update meeting type');
    }
  }

  /**
   * Delete a meeting type
   */
  async deleteMeetingType(token: string, id: string | number): Promise<void> {
    try {
      await axios.delete(
        `${API_URL}/meeting-types/${id}`,
        this.getAuthHeader(token)
      );
    } catch (error) {
      throw new Error('Failed to delete meeting type');
    }
  }

  /**
   * Create a single-use link by cloning an existing meeting type
   * This creates a complete copy with:
   * - All settings inherited from parent (or customized via customizations parameter)
   * - Unique timestamp-based slug
   * - isOneTimeUse flag set to true
   * - maxBookings set to 1
   *
   * @param token - Authentication token
   * @param id - Parent meeting type ID to clone from
   * @param customizations - Optional customizations to override parent settings
   */
  async createOneTimeLink(
    token: string,
    id: string | number,
    customizations?: Partial<CreateMeetingTypeDto>
  ): Promise<{ meetingType: MeetingType; oneTimeLink: string }> {
    try {
      const response = await axios.post(
        `${API_URL}/meeting-types/${id}/create-one-time-link`,
        customizations || {},
        this.getAuthHeader(token)
      );

      return {
        meetingType: response.data.meetingType,
        oneTimeLink: response.data.oneTimeLink
      };
    } catch (error) {
      throw new Error('Failed to create one-time link');
    }
  }

  /**
   * Duplicate a meeting type
   * Creates a full copy of an existing meeting type with all settings
   *
   * @param token - Authentication token
   * @param id - Meeting type ID to duplicate
   * @returns The duplicated meeting type
   */
  async duplicateMeetingType(
    token: string,
    id: string | number
  ): Promise<MeetingType> {
    try {
      const response = await axios.post(
        `${API_URL}/meeting-types/${id}/duplicate`,
        {},
        this.getAuthHeader(token)
      );

      return response.data.meetingType;
    } catch (error) {
      throw new Error('Failed to duplicate meeting type');
    }
  }

  /**
   * Last-second slot availability check for the booking page.
   *
   * The host's calendar is checked entirely on the server (the host's OAuth
   * token never reaches the browser). This is a best-effort pre-check: on any
   * error we return `true` so we never block a legitimate booking — the
   * authoritative conflict check runs again server-side at booking creation.
   *
   * Public endpoint: no auth token required.
   */
  async checkSlotAvailable(meetingTypeId: string, start: Date, end: Date): Promise<boolean> {
    try {
      const response = await axios.post(
        `${API_URL}/meeting-types/by-id/${meetingTypeId}/check-slot`,
        { start: start.toISOString(), end: end.toISOString() }
      );
      // Only an explicit `false` blocks; anything else is treated as available.
      return response.data?.available !== false;
    } catch {
      return true;
    }
  }

  /**
   * NORTH STAR: Cache management now handled by backend
   * Legacy method kept for backward compatibility during transition
   */
  clearCache(meetingTypeId?: number | string, dateKey?: string) {
    // NORTH STAR: All caching moved to backend with intelligent invalidation
    // Client-side cache clearing is no longer needed
  }

  /**
   * Get capacity for a specific event slot
   * Public endpoint - token is optional (required for admin features only)
   */
  async getSlotCapacity(
    token: string,
    meetingTypeId: number | string,
    slotStart: string,
    slotEnd: string
  ): Promise<{ capacity: number; bookedCount: number; available: number }> {
    try {
      // Make auth header optional - public booking pages don't have tokens
      const config = token ? this.getAuthHeader(token) : {};

      const response = await axios.get(
        `${API_URL}/meeting-types/${meetingTypeId}/capacity/${slotStart}/${slotEnd}`,
        config
      );
      return response.data;
    } catch (error: any) {
      throw new Error('Failed to fetch slot capacity');
    }
  }

  /**
   * Update capacity for a specific event slot
   */
  async updateSlotCapacity(
    token: string,
    meetingTypeId: number | string,
    slotStart: string,
    slotEnd: string,
    capacity: number
  ): Promise<void> {
    try {
      await axios.put(
        `${API_URL}/meeting-types/${meetingTypeId}/capacity/${slotStart}/${slotEnd}`,
        { capacity },
        this.getAuthHeader(token)
      );
    } catch (error: any) {
      throw new Error('Failed to update slot capacity');
    }
  }

  /**
   * Update default capacity for an event meeting type
   */
  async updateDefaultCapacity(
    token: string,
    meetingTypeId: number | string,
    maxCapacity: number
  ): Promise<void> {
    try {
      await axios.put(
        `${API_URL}/meeting-types/${meetingTypeId}/capacity/default`,
        { maxCapacity },
        this.getAuthHeader(token)
      );
    } catch (error: any) {
      throw new Error('Failed to update default capacity');
    }
  }

  /**
   * Get all slots with custom capacity overrides
   */
  async getSlotsWithCustomCapacity(
    token: string,
    meetingTypeId: number | string
  ): Promise<Array<{ slotStart: string; slotEnd: string; capacity: number; bookedCount: number }>> {
    try {
      const response = await axios.get(
        `${API_URL}/meeting-types/${meetingTypeId}/capacity/slots`,
        this.getAuthHeader(token)
      );
      return response.data;
    } catch (error: any) {
      throw new Error('Failed to fetch custom capacity slots');
    }
  }
}

const meetingTypeService = new MeetingTypeService();
export default meetingTypeService;
